/**
 * The page size a user last picked in any table's selector, remembered across tables and reloads
 * (PREC-4403). One key for the whole app: picking 100 on Contacts makes a list's New tab start at
 * 100.
 *
 * Read only by tables that offer a page-size selector, through `initialPageSize`: `useTableSearch`,
 * the Contacts and Tracker URL-state hooks, `useNonStickySearch({ rememberPageSize: true })` and the
 * change log. A list that only scrolls (Inbox, Outbox, Review-to-send, Compose) or shows a fixed
 * page (the feed, signals history) keeps its own size, so a pick on a table never enlarges their
 * requests. Written only by the two selectors (`TablePagination`, the MUI `TableComponent`), so a
 * size taken from a URL never replaces the user's own choice.
 *
 * Both selectors offer the same sizes, so a remembered size fits every table that reads it. Every
 * storage access is guarded: a private window, a full quota or blocked site data leaves a table at
 * its own default. In the Chrome extension the page's storage is already an in-memory stand-in
 * (`page-storage-sandbox.install.ts`), so the preference lasts for the session there.
 */
export const PAGE_SIZE_STORAGE_KEY = "signalist.table.pageSize";

/** The sizes both page-size selectors offer. */
export const PAGE_SIZE_OPTIONS = [10, 15, 25, 50, 100] as const;

const parsePageSize = (raw: string | null | undefined): number | null => {
	const size = Number(raw);

	return PAGE_SIZE_OPTIONS.some((option) => option === size) ? size : null;
};

/** The remembered size, or null when there is none or storage cannot be read. */
export const readPreferredPageSize = (): number | null => {
	try {
		return parsePageSize(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
	} catch {
		return null;
	}
};

/** Remembers a size the user picked in a selector. A failing storage is not the user's problem. */
export const rememberPageSize = (size: number): void => {
	if (parsePageSize(String(size)) === null) {
		return;
	}

	try {
		window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
	} catch {
		// Private window or full storage: the choice still applies to this table, just not later.
	}
};

interface InitialPageSizeInput {
	/** The `limit` the URL names, if any. */
	urlLimit?: string | null;
	/** The `page` the URL names, if any. */
	urlPage?: string | null;
	/** The table's own default size. */
	fallback: number;
}

/**
 * A selector table's page size when it mounts:
 * - a `limit` in the URL that a selector offers wins, so a shared link shows what it was shared
 *   with (a size no selector offers, `?limit=10000`, is ignored);
 * - a URL that names a later page but no size was written at the table's default (every table
 *   omitted the size while it was the default), so it reads as the default: replaying it at the
 *   remembered size would show other rows, or a page past the end;
 * - otherwise the remembered size, otherwise the table's default.
 */
export const initialPageSize = ({ urlLimit, urlPage, fallback }: InitialPageSizeInput): number => {
	const fromUrl = parsePageSize(urlLimit);

	if (fromUrl !== null) {
		return fromUrl;
	}

	if (Number(urlPage) > 1) {
		return fallback;
	}

	return readPreferredPageSize() ?? fallback;
};
